import React from 'react';
import { View, Text, Image, StyleSheet, Platform, Linking, Pressable } from 'react-native';
import { Bot } from '../../lib/icons';
import ChatAction from './ChatAction';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  avatarUrl?: string;
  onAvatarError?: () => void;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  attachmentSize?: number | null;
  attachmentType?: string | null;
  actions?: Array<{
    type: 'viewInventory' | 'viewInventoryItem' | 'viewMaintenancePlan' | 'createServiceRequest' | 'viewHoneyDoList';
    data?: {
      itemId?: string;
      itemName?: string;
    };
  }>;
  onBottomSheetOpen?: (type: string, data?: any) => void;
}

// Simple markdown renderer for React Native
const renderMarkdown = (text: string): React.ReactNode[] => {
  // Handle null/undefined content
  if (!text || typeof text !== 'string') {
    return [<Text key="empty" style={styles.markdownText}>No content</Text>];
  }

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  
  // Match markdown patterns
  const patterns = [
    { regex: /^### (.+)$/gm, component: (match: string, content: string) => (
      <Text key={`h3-${currentIndex++}`} style={styles.markdownH3}>{content}</Text>
    )},
    { regex: /^## (.+)$/gm, component: (match: string, content: string) => (
      <Text key={`h2-${currentIndex++}`} style={styles.markdownH2}>{content}</Text>
    )},
    { regex: /^# (.+)$/gm, component: (match: string, content: string) => (
      <Text key={`h1-${currentIndex++}`} style={styles.markdownH1}>{content}</Text>
    )},
    { regex: /\*\*(.+?)\*\*/g, component: (match: string, content: string) => (
      <Text key={`strong-${currentIndex++}`} style={styles.markdownStrong}>{content}</Text>
    )},
    { regex: /\*(.+?)\*/g, component: (match: string, content: string) => (
      <Text key={`em-${currentIndex++}`} style={styles.markdownEm}>{content}</Text>
    )},
    { regex: /`(.+?)`/g, component: (match: string, content: string) => (
      <Text key={`code-${currentIndex++}`} style={styles.markdownCode}>{content}</Text>
    )},
    { regex: /\[(.+?)\]\((.+?)\)/g, component: (match: string, text: string, url: string) => (
      <Text
        key={`link-${currentIndex++}`}
        style={styles.markdownLink}
        onPress={() => Linking.openURL(url)}
      >
        {text}
      </Text>
    )},
  ];

  // Split by lines first
  const lines = text.split('\n');
  
  lines.forEach((line, lineIndex) => {
    if (line.trim() === '') {
      parts.push(<Text key={`br-${lineIndex}`}>{'\n'}</Text>);
      return;
    }

    let processedLine = line;
    let lineParts: React.ReactNode[] = [];
    let textStart = 0;

    // Process inline patterns
    const inlinePatterns = [
      { regex: /\*\*(.+?)\*\*/g, style: styles.markdownStrong },
      { regex: /\*(.+?)\*/g, style: styles.markdownEm },
      { regex: /`(.+?)`/g, style: styles.markdownCode },
      { regex: /\[(.+?)\]\((.+?)\)/g, isLink: true },
    ];

    let lastIndex = 0;
    const matches: Array<{ start: number; end: number; component: React.ReactNode }> = [];

    // Find all matches
    inlinePatterns.forEach(pattern => {
      const regex = new RegExp(pattern.regex.source, 'g');
      let match;
      while ((match = regex.exec(processedLine)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        
        let component: React.ReactNode;
        if (pattern.isLink) {
          component = (
            <Text
              key={`link-${start}`}
              style={styles.markdownLink}
              onPress={() => Linking.openURL(match[2])}
            >
              {match[1]}
            </Text>
          );
        } else {
          component = (
            <Text key={`${pattern.regex.source}-${start}`} style={pattern.style}>
              {match[1]}
            </Text>
          );
        }
        
        matches.push({ start, end, component });
      }
    });

    // Sort matches by start position
    matches.sort((a, b) => a.start - b.start);

    // Build line with matches
    if (matches.length === 0) {
      // Check if it's a heading
      if (line.startsWith('### ')) {
        lineParts.push(
          <Text key={`h3-${lineIndex}`} style={styles.markdownH3}>
            {line.substring(4)}
          </Text>
        );
      } else if (line.startsWith('## ')) {
        lineParts.push(
          <Text key={`h2-${lineIndex}`} style={styles.markdownH2}>
            {line.substring(3)}
          </Text>
        );
      } else if (line.startsWith('# ')) {
        lineParts.push(
          <Text key={`h1-${lineIndex}`} style={styles.markdownH1}>
            {line.substring(2)}
          </Text>
        );
      } else {
        lineParts.push(
          <Text key={`text-${lineIndex}`} style={styles.markdownText}>
            {line}
          </Text>
        );
      }
    } else {
      // Process line with inline formatting
      let currentPos = 0;
      matches.forEach((match, matchIndex) => {
        // Add text before match
        if (match.start > currentPos) {
          lineParts.push(
            <Text key={`text-${lineIndex}-${matchIndex}`} style={styles.markdownText}>
              {processedLine.substring(currentPos, match.start)}
            </Text>
          );
        }
        // Add match component
        lineParts.push(match.component);
        currentPos = match.end;
      });
      // Add remaining text
      if (currentPos < processedLine.length) {
        lineParts.push(
          <Text key={`text-${lineIndex}-end`} style={styles.markdownText}>
            {processedLine.substring(currentPos)}
          </Text>
        );
      }
    }

    parts.push(
      <View key={`line-${lineIndex}`} style={styles.markdownLine}>
        {lineParts}
      </View>
    );
  });

  return parts;
};

const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  avatarUrl,
  onAvatarError,
  attachmentUrl,
  attachmentName,
  attachmentSize,
  attachmentType,
  actions,
  onBottomSheetOpen
}) => {
  const isAssistant = role === 'assistant';
  
  // Ensure content is a string
  const safeContent = content || '';

  const handleAttachmentPress = () => {
    if (attachmentUrl) {
      Linking.openURL(attachmentUrl);
    }
  };

  const isImage = attachmentUrl && attachmentType && attachmentType.startsWith('image/');

  return (
    <View
      style={[
        styles.container,
        !isAssistant && styles.containerReverse
      ]}
    >
      {isAssistant && (
        <View style={styles.avatarContainer}>
          {avatarUrl ? (
            <Image
              source={{ uri: avatarUrl }}
              style={styles.avatar}
              onError={onAvatarError}
            />
          ) : (
            <View style={styles.avatarFallback}>
              <Bot size={20} color="#ffffff" />
            </View>
          )}
        </View>
      )}
      <View style={styles.contentContainer}>
        <View style={[
          styles.messageBubble,
          isAssistant ? styles.assistantBubble : styles.userBubble
        ]}>
          {safeContent && (
            <View style={styles.markdownContainer}>
              {renderMarkdown(safeContent)}
            </View>
          )}
          {attachmentUrl && (
            <Pressable
              style={styles.attachmentContainer}
              onPress={handleAttachmentPress}
            >
              {isImage ? (
                <View style={styles.imageAttachmentContainer}>
                  <Image
                    source={{ uri: attachmentUrl }}
                    style={styles.imagePreview}
                    resizeMode="cover"
                  />
                  {attachmentName && (
                    <Text style={styles.imageAttachmentName} numberOfLines={1}>
                      {attachmentName}
                    </Text>
                  )}
                </View>
              ) : (
                <View style={styles.attachmentInfo}>
                  <Text style={styles.attachmentName} numberOfLines={1}>
                    {attachmentName || 'Attachment'}
                  </Text>
                  {attachmentSize && (
                    <Text style={styles.attachmentSize}>
                      {(attachmentSize / 1024).toFixed(1)} KB
                    </Text>
                  )}
                </View>
              )}
            </Pressable>
          )}
        </View>
        {isAssistant && actions && actions.length > 0 && (
          <View style={styles.actionsContainer}>
            {actions.map((action, index) => (
              <ChatAction key={index} {...action} onBottomSheetOpen={onBottomSheetOpen} />
            ))}
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  containerReverse: {
    flexDirection: 'row-reverse',
  },
  avatarContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#000000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  avatarFallback: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentContainer: {
    flex: 1,
    gap: 8,
  },
  messageBubble: {
    borderRadius: 12,
    padding: 16,
    alignSelf: 'flex-start',
  },
  assistantBubble: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  userBubble: {
    backgroundColor: '#dbeafe',
  },
  markdownContainer: {
    gap: 4,
  },
  markdownLine: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  markdownText: {
    fontSize: 14,
    color: '#111827',
    lineHeight: 20,
  },
  markdownH1: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
    marginTop: 4,
  },
  markdownH2: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 6,
    marginTop: 4,
  },
  markdownH3: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
    marginTop: 4,
  },
  markdownStrong: {
    fontWeight: '600',
    color: '#111827',
  },
  markdownEm: {
    fontStyle: 'italic',
    color: '#111827',
  },
  markdownCode: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
    fontSize: 13,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    color: '#374151',
  },
  markdownLink: {
    color: '#2563eb',
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  actionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  attachmentContainer: {
    marginTop: 8,
    padding: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  attachmentInfo: {
    gap: 4,
  },
  attachmentName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  attachmentSize: {
    fontSize: 12,
    color: '#6b7280',
  },
  imageAttachmentContainer: {
    gap: 8,
  },
  imagePreview: {
    width: '100%',
    maxWidth: 300,
    height: 200,
    borderRadius: 8,
    backgroundColor: '#f3f4f6',
  },
  imageAttachmentName: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
});

export default ChatMessage;
